/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import styled from "styled-components";
import type {
  CptCodeData,
  ProviderProcedureData,
} from "types/precalculatedAllowables.types";

interface ISpecialtyTabContentProps {
  cptCodes: CptCodeData[];
  specialty: string;
}

const Container = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const PlanLabel = styled.strong`
  font-size: 16px;
  font-weight: 600;
`;

const PlanInfo = styled.span`
  font-size: 16px;
  color: #777;
  margin-left: 4px;
`;

const ContractedRatesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const ContractedRateItem = styled.div`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    font-weight: 600;
    color: #333;
  }

  span {
    color: #777;
  }
`;

const VerticalBar = styled.span`
  color: #e0e0e0;
  font-size: 14px;
`;

const CalculationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CalculationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const CalculationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  strong {
    font-weight: 600;
    color: #333;
  }

  span {
    color: #777;
  }
`;

const CalculationBullet = styled.span`
  color: #777;
  font-size: 14px;
`;

const CalculationAmount = styled.div`
  min-width: 120px;
  text-align: right;
  font-weight: 500;
  color: #333;
`;

const SubtotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-top: 16px;
  border-top: 2px solid #333;
  font-size: 16px;
  font-weight: 600;
`;

const SubtotalLabel = styled.div`
  color: #333;
`;

const SubtotalAmount = styled.div`
  color: #333;
  font-size: 18px;
`;

/**
 * Format cents to currency string
 * @param cents - Amount in cents
 * @returns Formatted currency string (e.g., "$150.00")
 */
function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "N/A";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

/**
 * Component to display allowable amount calculation for a single specialty
 * Shows plan info, contracted rates, adjustment calculations, and subtotal
 */
export const SpecialtyTabContent: React.FC<ISpecialtyTabContentProps> =
  React.memo(({ cptCodes, specialty }) => {
    // Get payer name from first provider procedure (they all have the same payer)
    const payerName = useMemo(() => {
      if (cptCodes.length === 0) return "";
      const firstProcedure = cptCodes[0]?.providerProcedures?.[0];
      return firstProcedure?.payerName || "";
    }, [cptCodes]);

    // Calculate subtotal: for each CPT code, use only the HIGHEST-priced provider
    // If multiple providers have the same specialty, we only count the most expensive one
    const subtotal = useMemo(() => {
      return cptCodes.reduce((total, cptCode) => {
        const providersForSpecialty = cptCode.providerProcedures.filter(
          (pp) => pp.provider.specialty === specialty
        );

        if (providersForSpecialty.length === 0) return total;

        // Find the provider with the highest finalAmountInCents
        // Tie-breaker: alphabetical by provider name
        const highestPricedProvider = providersForSpecialty.reduce(
          (max, pp) => {
            if (pp.finalAmountInCents > max.finalAmountInCents) {
              return pp;
            }
            if (
              pp.finalAmountInCents === max.finalAmountInCents &&
              pp.provider.name.localeCompare(max.provider.name) < 0
            ) {
              return pp;
            }
            return max;
          }
        );

        return total + highestPricedProvider.finalAmountInCents;
      }, 0);
    }, [cptCodes, specialty]);

    // Build contracted rates list: Array of {cptCode, providerName, rate}
    // Shows ALL providers for this specialty, not just the first one
    const contractedRates = useMemo(() => {
      const ratesList: Array<{
        cptCode: string;
        providerName: string;
        rate: number;
      }> = [];

      cptCodes.forEach((cptCode) => {
        cptCode.providerProcedures
          .filter((pp) => pp.provider.specialty === specialty)
          .forEach((pp) => {
            ratesList.push({
              cptCode: cptCode.code,
              providerName: pp.provider.name,
              rate: pp.priceInCents,
            });
          });
      });

      return ratesList;
    }, [cptCodes, specialty]);

    // Get adjustments for this specialty, but only from the highest-priced provider per CPT code
    // This matches the logic used in the subtotal calculation
    const allAdjustments = useMemo(() => {
      return cptCodes.flatMap((cptCode) => {
        const providersForSpecialty = cptCode.providerProcedures.filter(
          (pp) => pp.provider.specialty === specialty
        );

        if (providersForSpecialty.length === 0) return [];

        // Find the provider with the highest finalAmountInCents (same logic as subtotal)
        // Tie-breaker: alphabetical by provider name
        const highestPricedProvider = providersForSpecialty.reduce(
          (max, pp) => {
            if (pp.finalAmountInCents > max.finalAmountInCents) {
              return pp;
            }
            if (
              pp.finalAmountInCents === max.finalAmountInCents &&
              pp.provider.name.localeCompare(max.provider.name) < 0
            ) {
              return pp;
            }
            return max;
          }
        );

        // Only return adjustments from the highest-priced provider
        return highestPricedProvider.adjustments.map((adj) => ({
          cptCode: cptCode.code,
          type: adj.type,
          description: adj.description,
          amountInCents: adj.amountInCents,
        }));
      });
    }, [cptCodes, specialty]);

    if (cptCodes.length === 0) {
      return (
        <Container>
          <div>No data available for this specialty</div>
        </Container>
      );
    }

    return (
      <Container>
        {/* Plan Information Section */}
        {payerName && (
          <Section className="flex">
            <PlanLabel>Plan:</PlanLabel>
            <PlanInfo>{payerName}</PlanInfo>
          </Section>
        )}

        {/* Contracted Rates Section */}
        <Section>
          <SectionTitle>Contracted rates:</SectionTitle>
          <ContractedRatesList>
            {contractedRates.map((item, idx) => (
              <React.Fragment
                key={`${item.cptCode}-${item.providerName}-${idx}`}
              >
                {idx > 0 && <VerticalBar>|</VerticalBar>}
                <ContractedRateItem>
                  <strong>{item.cptCode}</strong>
                  <span>({item.providerName}):</span>
                  <span>{formatCurrency(item.rate)}</span>
                </ContractedRateItem>
              </React.Fragment>
            ))}
          </ContractedRatesList>
        </Section>

        {/* Calculation Section */}
        <Section>
          <SectionTitle>Calculation:</SectionTitle>
          <CalculationList>
            {allAdjustments.length === 0 ? (
              <CalculationRow>
                <CalculationLeft>
                  <span>No adjustments applied</span>
                </CalculationLeft>
              </CalculationRow>
            ) : (
              allAdjustments.map((adj, idx) => (
                <CalculationRow key={idx}>
                  <CalculationLeft>
                    <strong>{adj.cptCode}</strong>
                    <CalculationBullet>•</CalculationBullet>
                    <span>{adj.description}</span>
                  </CalculationLeft>
                  <CalculationAmount>
                    {formatCurrency(adj.amountInCents)}
                  </CalculationAmount>
                </CalculationRow>
              ))
            )}
          </CalculationList>
        </Section>

        {/* Subtotal */}
        <SubtotalRow>
          <SubtotalLabel>Subtotal:</SubtotalLabel>
          <SubtotalAmount>{formatCurrency(subtotal)}</SubtotalAmount>
        </SubtotalRow>
      </Container>
    );
  });
